import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  getDocuments,
  filterDocumentsByName,
  getClubsByUser,
} from "../../data/services/document_service";
import DocumentModel from "../../data/models/document";
import { CREW_LOGIN, CREW_PAGE } from "../../core/app-urls";
import { FaArrowLeft } from "react-icons/fa";
import ClubsUserModel from "../../data/models/clubsUser";
import { fetchWithAuth } from "../../utils/openDocuments";

const DocumentStyle = styled.div`
  min-height: 100vh;
  width: 100%;
  background-color: #e5f8fa;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding-bottom: 50px;
`;

const WrapContent = styled.div`
  margin-top: 100px;
  width: 75%;
  height: 80%;
  display: flex;
  flex-direction: column;
  /* background-color: red; */

  h1 {
    margin: -10px 9px;
    padding: 0;
    color: #ff5742;
  }

  h2 {
    color: black;
    font-size: 15px;
    margin-top: auto;
  }

  h3 {
    color: #005ca9;
    font-size: 30px;
    margin-left: 9px;
    margin-top: 10px;
  }
`;

const DocumentsContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const SearchContainer = styled.div`
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SelectClub = styled.select`
  padding: 12px 16px;
  width: auto;
  max-width: 75%;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
`;

const SearchInput = styled.input`
  padding: 12px 16px;
  width: 40%;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: #007bff;
  }

  &::placeholder {
    color: #999;
  }
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const DocumentCard = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  font-size: 14px;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 120px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`;

const DocumentTitle = styled.h3`
  margin: 0 0 12px 0;
  color: #333;
  font-size: 14px;
  word-break: break-all;
`;

const DocumentActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: auto;
  padding-top: 15px;
`;

const ActionButton = styled.a`
  padding: 8px 16px;
  background: #007bff;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-size: 14px;
  transition: background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background: #0056b3;
  }
`;

const ViewButton = styled(ActionButton)`
  background: #28a745;
  cursor: pointer;

  &:hover {
    background: #1e7e34;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 18px;
  color: #666;
`;

const NoDocumentsMessage = styled.div`
  text-align: center;
  padding: 40px;
  font-size: 14px;
  color: #999;
`;

const ResultsInfo = styled.div`
  margin-bottom: 20px;
  color: #666;
  font-size: 14px;
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  background: none;
  border: none;
  color: #005ca9;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: #003f7d;
  }
`;

export default function Documents() {
  const [documents, setDocuments] = useState<DocumentModel[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<DocumentModel[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [clubs, setClubs] = useState<ClubsUserModel[]>([]);
  const [selectedClub, setSelectedClub] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      navigate(CREW_LOGIN);
      return;
    }

    const loadClubs = async () => {
      const { clubs } = await getClubsByUser();
      setClubs(clubs);

      if (clubs.length === 1) {
        setSelectedClub(clubs[0].prefix!);
      }
    };

    loadClubs();
  }, [navigate]);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        if (!selectedClub) {
          setDocuments([]);
          setFilteredDocuments([]);
          setTotal(0);
          return;
        }

        const { documents: docs, total: totalDocs } = await getDocuments(
          selectedClub
        );

        setDocuments(docs);
        setFilteredDocuments(docs);
        setTotal(totalDocs);
      } catch (error) {
        console.error("Erro ao buscar documentos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [selectedClub]);

  useEffect(() => {
    const filtered = filterDocumentsByName(documents, searchTerm);
    setFilteredDocuments(filtered);
  }, [documents, searchTerm]);

  return (
    <DocumentStyle>
      <BackButton onClick={() => navigate(CREW_PAGE)}>
        <FaArrowLeft />
        Voltar
      </BackButton>

      <WrapContent>
        <h1>PORTAL DO CLIENTE</h1>
        <h3>Documentos</h3>

        <DocumentsContainer>
          <SearchContainer>
            <SelectClub
              value={selectedClub}
              onChange={(e) => setSelectedClub(e.target.value)}
              disabled={clubs.length === 1}
            >
              <option value="">Selecione um Clube</option>

              {clubs.map((club) => (
                <option key={club.id_clube} value={club.prefix!}>
                  {club.nome_clube}
                </option>
              ))}
            </SelectClub>

            <SearchInput
              type="text"
              placeholder="Buscar documentos por nome do arquivo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!selectedClub}
            />
          </SearchContainer>

          {!selectedClub ? (
            <NoDocumentsMessage>
              Selecione um clube para listar os documentos.
            </NoDocumentsMessage>
          ) : loading ? (
            <LoadingMessage>Carregando documentos...</LoadingMessage>
          ) : filteredDocuments.length === 0 ? (
            <NoDocumentsMessage>
              {searchTerm
                ? `Nenhum documento encontrado para "${searchTerm}"`
                : "Nenhum documento encontrado"}
            </NoDocumentsMessage>
          ) : (
            <DocumentsGrid>
              {filteredDocuments.map((doc, index) => (
                <DocumentCard key={index}>
                  <DocumentTitle>{doc.filename}</DocumentTitle>

                  <DocumentActions>
                    <ViewButton
                      onClick={() =>
                        fetchWithAuth(doc.url!, doc.filename!, true)
                      }
                    >
                      Visualizar
                    </ViewButton>
                    <ActionButton
                      onClick={() =>
                        fetchWithAuth(doc.downloadUrl!, doc.filename!)
                      }
                    >
                      Download
                    </ActionButton>
                  </DocumentActions>
                </DocumentCard>
              ))}
            </DocumentsGrid>
          )}
        </DocumentsContainer>
      </WrapContent>
    </DocumentStyle>
  );
}
